import { useEffect, useState } from "react";
import { Download, FileWarning, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, docUrl } from "@/lib/api";
import { btn } from "@/components/common/Ornament";

function CsvPreview({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/documents/${id}/view`, { responseType: "text" }).then((r) => {
      setRows(String(r.data).split(/\r?\n/).filter(Boolean).slice(0, 300).map((l) => l.split(",")));
    }).catch(() => setRows([]));
  }, [id]);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-[#C59B27] m-8" />;
  return (
    <div className="overflow-auto h-full bg-white">
      <table className="text-xs min-w-full">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i === 0 ? "bg-[#0F2E23] text-[#F9F6F0] font-semibold" : "border-b border-[#E2DCD2]"}>
              {r.map((c, k) => <td key={k} className="px-3 py-1.5 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Body({ doc }) {
  const src = docUrl(doc.id);
  if (doc.ext === "pdf") return <iframe src={`${src}#toolbar=0&navpanes=0`} title={doc.name} className="w-full h-full bg-white" data-testid="doc-preview-pdf" />;
  if (["jpg", "jpeg", "png"].includes(doc.ext)) return <div className="h-full flex items-center justify-center bg-[#1A1D1A]"><img src={src} alt={doc.name} className="max-h-full max-w-full object-contain" data-testid="doc-preview-image" /></div>;
  if (doc.ext === "mp4") return <video src={src} controls controlsList={doc.downloadable ? undefined : "nodownload"} className="w-full h-full bg-black" data-testid="doc-preview-video" />;
  if (doc.ext === "csv") return <CsvPreview id={doc.id} />;
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-white" data-testid="doc-preview-unsupported">
      <FileWarning className="h-10 w-10 text-[#C59B27]" />
      <p className="mt-4 font-semibold text-[#0F2E23]">In-browser preview isn't available for .{doc.ext} files.</p>
      <p className="text-sm text-slate-500 mt-1">{doc.downloadable ? "Use the download button to open it." : "Contact the sponsor if you need access to this file."}</p>
    </div>
  );
}

export default function DocPreview({ doc, canDownload, onClose }) {
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[96vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="doc-preview-modal">
        {doc && (
          <>
            <DialogHeader className="px-5 py-3 border-b border-[#E2DCD2] flex-row items-center justify-between space-y-0 pr-12">
              <div className="min-w-0">
                <DialogTitle className="font-display text-xl text-[#0F2E23] truncate">{doc.name}</DialogTitle>
                <DialogDescription className="text-xs">Confidential – NOLA Project</DialogDescription>
              </div>
              {canDownload && <a href={docUrl(doc.id, true)} className={btn.gold + " !h-9 shrink-0"} data-testid="doc-download-btn"><Download className="h-4 w-4" /> Download</a>}
            </DialogHeader>
            <div className="relative flex-1 min-h-0">
              <Body doc={doc} />
              <div className="watermark" data-testid="doc-watermark-overlay" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
